import React from 'react';
import { FaVolumeUp, FaSpinner } from 'react-icons/fa';
import MobileChatBubble from './MobileChatBubble';
import ReportButton from './reports/ReportButton';
import MaskedBlock from './reports/MaskedBlock';
import { safeRenderAI, isMessageBlocked } from '../lib/safeRenderAI';

interface ChatBubbleProps {
  message: {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    audio_url?: string;
    created_at: string;
  };
  user: {
    id: string;
    email: string;
    profile_image?: string | null;
  } | null;
  isPlaying: boolean;
  playingMessageId: string | null;
  onPlayAudio: (messageId: string, audioUrl: string) => void;
  generatingAudio: boolean;
  onReportModalToggle?: (isOpen: boolean) => void;
}

interface SafetyState {
  isSafe: boolean;
  flaggedCategories: string[];
  isBlocked: boolean;
}

// Hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  user,
  isPlaying,
  playingMessageId,
  onPlayAudio,
  generatingAudio,
  onReportModalToggle
}) => {
  const [safetyState, setSafetyState] = React.useState<SafetyState>({
    isSafe: true,
    flaggedCategories: [],
    isBlocked: false
  });
  const [safetyChecked, setSafetyChecked] = React.useState(false);

  const isMobile = useIsMobile();
  
  // Check content safety for AI messages
  React.useEffect(() => {
    if (message.role === 'assistant' && !safetyChecked) {
      checkContentSafety();
    }
  }, [message.content, message.role, safetyChecked]);

  const checkContentSafety = async () => {
    try {
      const [moderationResult, blockedStatus] = await Promise.all([
        safeRenderAI(message.content),
        isMessageBlocked(message.id)
      ]);

      setSafetyState({
        isSafe: moderationResult.isSafe,
        flaggedCategories: moderationResult.flaggedCategories,
        isBlocked: blockedStatus
      });
    } catch (error) {
      console.error('Error checking content safety:', error);
      // Default to safe if check fails
      setSafetyState({
        isSafe: true,
        flaggedCategories: [],
        isBlocked: false
      });
    } finally {
      setSafetyChecked(true);
    }
  };

  // Use mobile component on small screens
  if (isMobile) {
    return (
      <MobileChatBubble {...{ 
        message, 
        user, 
        isPlaying, 
        playingMessageId, 
        onPlayAudio, 
        generatingAudio,
        safetyState,
        safetyChecked 
      }} />
    );
  }
  
  const isUser = message.role === 'user';
  const isCurrentlyPlaying = playingMessageId === message.id && isPlaying;

  // AI Teacher profile image
  const aiAvatarUrl = "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/Public/image_681cd52d835cd.jpg";

  // Format AI response to remove markdown and improve readability
  const formatAIResponse = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
      .replace(/\*(.*?)\*/g, '$1') // Remove italic markdown
      .replace(/#{1,6}\s/g, '') // Remove heading markdown
      .replace(/`(.*?)`/g, '$1') // Remove code markdown
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove link markdown
      .trim();
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex items-start space-x-2 sm:space-x-3 max-w-[85%] sm:max-w-[80%] ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-white/20 bg-gray-800">
            {isUser ? (
              user?.profile_image ? (
                <img 
                  src={user.profile_image} 
                  alt="User"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-yellow-600 text-black font-bold text-sm sm:text-base">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
              )
            ) : (
              <img 
                src={aiAvatarUrl}
                alt="AI Teacher"
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        {/* Message Content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} min-w-0 flex-1`}>
          {/* Message Bubble */}
          {!isUser && (!safetyState.isSafe || safetyState.isBlocked) && safetyChecked ? (
            <MaskedBlock 
              reason={safetyState.isBlocked ? 'Contenu bloqué par modération' : 'Contenu potentiellement inapproprié'}
              isBlocked={safetyState.isBlocked}
              className="max-w-full"
            >
              <div className={`relative p-3 sm:p-4 rounded-2xl max-w-full break-words bg-white/10 backdrop-blur-lg text-white border border-white/10 rounded-bl-md`}>
                <div className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                  {formatAIResponse(message.content)}
                </div>
              </div>
            </MaskedBlock>
          ) : (
            <div className={`relative p-3 sm:p-4 rounded-2xl max-w-full break-words ${
              isUser 
                ? 'bg-yellow-500 text-black rounded-br-md' 
                : 'bg-white/10 backdrop-blur-lg text-white border border-white/10 rounded-bl-md'
            }`}>
              {/* Message Text */}
              <div className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                {isUser ? message.content : formatAIResponse(message.content)}
              </div>
            </div>
          )}

          {/* Audio Controls and Report Button for AI messages */}
          {!isUser && (
            <div className="mt-2 flex items-center justify-between w-full">
              {/* Audio Controls */}
              {message.audio_url && (
                <button
                  onClick={() => onPlayAudio(message.id, message.audio_url!)}
                  disabled={generatingAudio}
                  className="flex items-center space-x-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 text-xs sm:text-sm"
                >
                  {generatingAudio ? (
                    <FaSpinner className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                  ) : (
                    <FaVolumeUp className={`w-3 h-3 sm:w-4 sm:h-4 ${isCurrentlyPlaying ? 'text-yellow-400' : ''}`} />
                  )}
                  <span className={isCurrentlyPlaying ? 'text-yellow-400' : ''}>
                    {generatingAudio ? 'Generating...' : isCurrentlyPlaying ? 'Playing...' : 'Listen'}
                  </span>
                </button>
              )}
              
              {/* Report Button */}
              <ReportButton
                contentId={message.id}
                contentPreview={message.content}
                variant="A"
                className="ml-auto"
                onModalToggle={onReportModalToggle}
              />
            </div>
          )}

          {/* Timestamp */}
          <div className={`mt-1 text-xs text-white/50 ${isUser ? 'text-right' : 'text-left'}`}>
            {new Date(message.created_at).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;